import { Component } from '@theme/component';

const KEY = 'lea:mediaView';

/**
 * Flips every product card in the grid between its front and its rear view.
 *
 * The rear image is a NAMED MEDIA ROLE read from a product metafield, never a
 * position in product.media: merchants reorder images routinely, and image two is
 * usually a detail crop. A product with no rear media renders front only and simply
 * does not respond to this control.
 *
 * @extends {Component<HTMLElement>}
 */
class LeaMediaToggle extends Component {
  requiredRefs = ['front', 'back'];

  connectedCallback() {
    super.connectedCallback();
    // Nothing is moved or built here. The control and the rail head are both
    // server-rendered; this component only flips the grid.
    let stored = null;
    try {
      stored = localStorage.getItem(KEY);
    } catch {
      /* storage unavailable — fall through to the default view */
    }
    this.#apply(stored === 'back' ? 'back' : 'front');
  }

  /**
   * On a collection page the drawn toolbar is ONE row: the count, this control
   * and the sort, all on the same line. Horizon renders the count and the sort
   * inside the filters block, `main-collection` accepts no blocks of its own, and
   * this control has to live in a section — so the markup cannot be authored into
   * that row. It moves itself there instead, ahead of the sort.
   *
   * Where there is no filters bar, as on the Home band, it stays where it is.
   *
   * The destination is put into the bar BEFORE anything moves into it: an
   * insertion that throws once left this control in a detached node and the
   * toggle disappeared from the page entirely.
   */


  showFront = () => this.#apply('front', true);
  showBack = () => this.#apply('back', true);

  /**
   * @param {'front'|'back'} view
   * @param {boolean} [persist]
   */
  #apply(view, persist = false) {
    document.documentElement.setAttribute('data-lea-view', view);
    const isBack = view === 'back';
    this.refs.front?.setAttribute('aria-pressed', String(!isBack));
    this.refs.back?.setAttribute('aria-pressed', String(isBack));
    if (!persist) return;
    try {
      localStorage.setItem(KEY, view);
    } catch {
      /* storage unavailable — the choice simply does not survive navigation */
    }
  }
}

if (!customElements.get('lea-media-toggle')) {
  customElements.define('lea-media-toggle', LeaMediaToggle);
}
